import { createContext, PropsWithChildren, useContext, useEffect, useId, useState } from 'react';
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import GalleryDots from "./GalleryDots";

interface ImageData {
    src: string;
    alt: string;
    header: string;
}

interface ImageWithModalContextValue {
    imageData: Record<string, ImageData>;
    registerImage: (id: string, data: ImageData) => void;
    removeImage: (id: string) => void;
    openImage: (id: string) => void;
}

const ImageWithModalContext = createContext<ImageWithModalContextValue>({
    imageData: {},
    registerImage: () => {},
    removeImage: () => {},
    openImage: () => {},
});

/**
 * Displays an image that opens in a modal gallery when clicked.
 * Must be used inside an `ImageWithModal.Provider`.
 */
export default function ImageWithModal({ src, height = undefined, className, alt, header }: {
    /** Image source URL. */
    src: string;
    /** Optional height constraint in pixels. */
    height?: number;
    /** Additional CSS class names applied to the image element. */
    className?: string;
    /** Alt text for the image. */
    alt: string;
    /** Title displayed in the modal header. */
    header: string;
}) {
    const id = useId();
    const { registerImage, removeImage, openImage } = useContext(ImageWithModalContext);

    useEffect(() => {
        registerImage(id, { src, alt, header });
        return () => {
            removeImage(id);
        }
    }, [id, src, alt, header])

    return (
        <img
            src={src}
            alt={alt}
            height={height}
            className={`cursor-pointer ${className}`}
            onClick={() => openImage(id)}
        />
    );
}

/**
 * Provides the image gallery context for all child `ImageWithModal` components
 * and renders a modal with previous/next navigation when an image is opened.
 */
function ImageWithModalProvider({ children }: PropsWithChildren) {
    const [imageData, setImageData] = useState<Record<string, ImageData>>({});
    const [openId, setOpenId] = useState<string | undefined>(undefined);

    const ids = Object.keys(imageData);
    const activeIndex = ids.indexOf(openId ?? '');
    const getNextIndex = (idx: number) => idx < ids.length - 1 ? idx + 1 : 0;
    const getPrevIndex = (idx: number) => idx > 0 ? idx - 1 : ids.length - 1;

    const prevId = openId ? ids[getPrevIndex(activeIndex)] : undefined;
    const nextId = openId ? ids[getNextIndex(activeIndex)] : undefined;

    return (
        <ImageWithModalContext.Provider
            value={{
                imageData,
                registerImage: (id, data) => {
                    setImageData((oldData) => ({ ...oldData, [id]: data }));
                },
                removeImage: (id) => {
                    setImageData((oldData) => {
                        delete oldData[id];
                        return oldData;
                    });
                },
                openImage: (id) => {
                    setOpenId(id);
                },
            }}
        >
            {children}
            {openId && imageData[openId] && (
                <Modal
                    onHide={() => setOpenId(undefined)}
                    show
                    size="xl"
                >
                    <Modal.Header closeButton className="text-center d-flex">
                        <Modal.Title className="flex-grow-1 justify-content-center">{imageData[openId].header}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="bg-lightest">
                        <div className="d-flex justify-content-center mx-auto align-content-center align-items-center gap-3 mb-2">
                            <Button onClick={() => setOpenId(prevId)} className="text-sm text-truncate" style={{ width: '200px' }}>
                                Prev: {prevId && imageData[prevId].header}
                            </Button>
                            <GalleryDots length={ids.length} activeIndex={activeIndex} />
                            <Button onClick={() => setOpenId(nextId)} className="text-sm text-truncate" style={{ width: '200px' }}>
                                Next: {nextId && imageData[nextId].header}
                            </Button>
                        </div>
                        <img className="rounded-3 shadow" src={imageData[openId].src} alt={imageData[openId].alt} width="100%" />
                    </Modal.Body>
                </Modal>
            )}
        </ImageWithModalContext.Provider>
    );
}

ImageWithModal.Provider = ImageWithModalProvider;
